import Accordion from "@/components/Accordion"
import Layout from "@/components/layouts/DefaultLayout"

/**
 * Linux instructions component moved from FirmwarePage to be reusable
 */
const LinuxUdevInstructions = () => {
  return (
    <div className="space-y-4">
      <p>
        To install firmware updates, you must grant permissions to Element IV's DFU interface.
        Follow these steps:
      </p>

      <ol className="list-inside list-decimal space-y-4">
        <li>
          <strong>Create the udev rule file:</strong>
          <pre className="mt-1 rounded border border-mine-shaft bg-jaguar p-2">
            sudo nano /etc/udev/rules.d/50-jdslabs-dfu.rules
          </pre>
        </li>
        <li>
          <strong>Add these rules to the file to reference the Element IV DFU interface:</strong>
          <pre className="mt-1 rounded border border-mine-shaft bg-jaguar p-2">
            {'SUBSYSTEM=="usb", ATTR{idVendor}=="152a", ATTR{idProduct}=="88fa", TAG+="uaccess"\n'}
            {'SUBSYSTEM=="usb", ATTR{idVendor}=="152a", ATTR{idProduct}=="88fc", TAG+="uaccess"'}
          </pre>
        </li>
        <li>
          <strong>Reload the rules and apply changes:</strong>
          <pre className="mt-1 rounded border border-mine-shaft bg-jaguar p-2">
            sudo udevadm control --reload-rules{"\n"}
            sudo udevadm trigger
          </pre>
        </li>
        <li>
          <strong>Unplug and reconnect your device</strong>
        </li>
        <li>
          If issues persist, try rebooting your system:
          <pre className="mt-1 rounded border border-mine-shaft bg-jaguar p-2">sudo reboot</pre>
        </li>
      </ol>

      <div className="mt-6 border-t border-mine-shaft pt-4">
        <p className="mb-2 text-spun-pearl">
          <strong>Note:</strong> While it is entirely safe to keep these permissions enabled for
          future firmware updates, you may remove them using the following:
        </p>
        <pre className="mt-1 rounded border border-mine-shaft bg-jaguar p-2">
          sudo rm /etc/udev/rules.d/50-jdslabs-dfu.rules && sudo udevadm control --reload-rules
        </pre>
      </div>
    </div>
  )
}

const FaqPage = () => {
  return (
    <Layout>
      <div className="relative w-full flex-1 overflow-y-auto px-20 py-14">
        <h3 className="type-heading-2xl font-bold">Frequently Asked Questions</h3>

        <div className="type-body-lg mt-5">
          Find answers to common questions and setup instructions below.
        </div>

        <Accordion
          className="mb-32 mt-12"
          defaultExpandedKeys={new Set([])}
          items={[
            {
              title: "Supported Browsers",
              content: (
                <div className="space-y-4">
                  <p>JDS Labs Core is supported on the following desktop browsers:</p>
                  <ul className="list-disc pl-5">
                    <li>Google Chrome</li>
                    <li>Chromium</li>
                    <li>Microsoft Edge</li>
                    <li>Opera</li>
                    <li>Brave</li>
                    <li>
                      Firefox*{" "}
                      <span className="text-sm italic">
                        requires the{" "}
                        <a
                          href="https://addons.mozilla.org/en-US/firefox/addon/webserial-for-firefox/"
                          className="text-red hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          WebSerial for Firefox
                        </a>{" "}
                        extension. Please note that Firefox does not support WebUSB (only required
                        for firmware updates).
                      </span>
                    </li>
                  </ul>
                  <p className="mt-4 text-sm italic">
                    Note: Safari and mobile browsers are not currently supported due to WebUSB
                    limitations.
                  </p>
                </div>
              ),
            },
            {
              title: "Linux: Unable to connect",
              content: (
                <div className="space-y-4">
                  <p>
                    Linux distros require the following permissions changes to see the serial
                    device.{" "}
                    <strong>
                      Remember to log out and log back in after making these changes (or reboot):
                    </strong>
                  </p>
                  <pre className="mt-1 rounded border border-mine-shaft bg-jaguar p-2">
                    sudo usermod -aG uucp,dialout $USER
                  </pre>
                  <p>
                    <i>
                      If your system does not have a dialout group, simply omit ",dialout" from the
                      above command.
                    </i>
                  </p>
                  <p>For Chromium, you may also need to run the following:</p>
                  <pre className="mt-1 rounded border border-mine-shaft bg-jaguar p-2">
                    sudo snap connect chromium:raw-usb
                  </pre>

                  <p className="mt-2">
                    You can verify that your Element IV serial device is recognized by running:
                  </p>
                  <pre className="mt-1 rounded border border-mine-shaft bg-jaguar p-2">
                    ls /dev/tty*
                  </pre>
                </div>
              ),
            },
            {
              title: "Linux: How to update Element IV Firmware",
              content: <LinuxUdevInstructions />,
            },
            {
              title: "How to Backup and Restore Device Presets",
              content: (
                <div className="space-y-6">
                  <p>
                    We recommend backing up your presets before updating firmware. You may either
                    backup to your Core Cloud account, or to your local device.
                  </p>

                  <div>
                    <h4 className="mb-3 font-bold">Cloud Backup (preferred):</h4>
                    <ol className="list-inside list-decimal space-y-2">
                      <li>
                        Login to your free Core account (create an account if you haven't already).
                      </li>
                      <li>Select a desired Element IV preset.</li>
                      <li>
                        Click the "Save as New Preset" button found above the equalizer canvas.
                      </li>
                      <li>
                        Type a name and add optional details. Leave the "Also save to Element IV"
                        checkbox unchecked.
                      </li>
                      <li>Click "Save"</li>
                      <li>Repeat steps 2-5 for each preset you want to backup.</li>
                    </ol>

                    <h5 className="mb-2 mt-4 font-semibold">
                      Restoring Cloud Backups to Element IV:
                    </h5>
                    <ol className="list-inside list-decimal space-y-2">
                      <li>Click "Add New" from the Element IV Storage list.</li>
                      <li>
                        Select the preset you wish to save to your Element IV from your list of
                        Cloud presets.
                      </li>
                      <li>Repeat the above for each preset, as needed.</li>
                    </ol>
                  </div>

                  <hr className="border-mine-shaft" />

                  <div>
                    <h4 className="mb-3 font-bold">Offline Backup:</h4>
                    <ol className="list-inside list-decimal space-y-2">
                      <li>Select the desired preset to backup.</li>
                      <li>Click the "Export" button found above the equalizer canvas</li>
                      <li>Click "Export Active Profile as a File"</li>
                      <li>Repeat steps 1-3 for each preset you want to backup.</li>
                    </ol>

                    <h5 className="mb-2 mt-4 font-semibold">
                      Restoring Presets from backup files:
                    </h5>
                    <ol className="list-inside list-decimal space-y-2">
                      <li>Click the "Import" button found above the equalizer canvas</li>
                      <li>Browse and select your saved preset file</li>
                      <li>The preset will be loaded into the equalizer interface</li>
                      <li>Click "Add New" from the Element IV Storage list.</li>
                      <li>Type a preset name and click "Save to Device".</li>
                      <li>Repeat the above for each preset, as needed.</li>
                    </ol>
                  </div>
                </div>
              ),
            },
            // Add more FAQ items here as needed
          ]}
        />
      </div>
    </Layout>
  )
}

export default FaqPage
